import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, LoaderIcon, TruckIcon } from 'lucide-react';
import { builderAssets, builderPrices, builderSteps, completeProtection, essentialProtection, lensOptions, remainingLenses } from '../../data/builder';
import { BuilderApi } from '../../hooks/useBuilder';
import { protectionLabel } from '../../utils/builderCart';
import { money } from '../QuantityStepper';
import { RxMark } from '../PrescriptionBadge';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
import { BuilderOptionCard } from './BuilderOptionCard';
import { X1LensImage } from './BuilderVisual';
import { NotConnected } from './BuilderSummary';
interface BuilderStepPanelProps {
  builder: BuilderApi;
}
type AddOnStep = 'lensSystem' | 'case' | 'care' | 'rx';
const EASE = [0.23, 1, 0.32, 1] as const;
const titleClass = 'font-display text-3xl font-extrabold uppercase leading-none text-offwhite';
function Thumb({
  src,
  alt,
  filter
}: {
  src: string;
  alt: string;
  filter?: string;
}) {
  return <span className="block h-16 w-16 overflow-hidden border border-white/10 bg-ink-950 sm:h-20 sm:w-20" data-id="element-82" data-mp="jo2ruspcx4kc">
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" style={filter ? {
      filter
    } : undefined} data-id="element-83" data-mp="foar3267g2pj" />
    </span>;
}

/** "Add" / "No thanks" for an optional step. Both move the build forward. */
function Decision({
  builder,
  kind,
  selected,
  included,
  addLabel,
  declineLabel,
  price
}: {
  builder: BuilderApi;
  kind: AddOnStep;
  selected: boolean;
  included: boolean;
  addLabel: string;
  declineLabel: string;
  price: number;
}) {
  const primary = 'inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite';
  const secondary = 'inline-flex items-center justify-center px-4 py-3.5 font-display text-base font-bold uppercase leading-none tracking-wide text-steel-200 border border-white/20 transition-[border-color,color] duration-150 ease-grit hover:border-white/40 hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500';
  if (included) {
    return <div className="mt-5 flex" data-id="element-84" data-mp="1mkb4l03dr92e">
        <button type="button" onClick={builder.nextStep} className={primary} data-id="element-85" data-mp="1rhumc9zae3z3">
          Included · Next
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-86" data-mp="1dibpidbxef23" />
        </button>
      </div>;
  }
  return <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-87" data-mp="1hk6nb7uneab4">
      {selected ? <>
          <button type="button" onClick={builder.nextStep} className={primary} data-id="element-88" data-mp="37wmf4o0chd8x">
            <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-89" data-mp="3bxogpbhqi1e2" />
            Added · Next
          </button>
          <button type="button" onClick={() => builder.decide(kind, false)} className={secondary} data-id="element-90" data-mp="10auo1nabhdry">
            Remove
          </button>
        </> : <>
          <button type="button" onClick={() => builder.decide(kind, true)} className={primary} data-id="element-91" data-mp="1a05v74dn88d7">
            {addLabel} · +{money(price)}
          </button>
          <button type="button" onClick={() => builder.decide(kind, false)} className={secondary} data-id="element-92" data-mp="157l4ibq1abdw">
            {declineLabel}
          </button>
        </>}
    </div>;
}
export function BuilderStepPanel({
  builder
}: BuilderStepPanelProps) {
  const reduce = useReducedMotion();
  const {
    state,
    step,
    lines,
    total
  } = builder;
  const index = builderSteps.findIndex(item => item.id === step);
  const primaryLens = lensOptions.find(lens => lens.id === state.lens);
  return <div className="border border-white/10 bg-ink-900" data-id="element-93" data-mp="3uqdcwwvil6ms">
      <AnimatePresence mode="wait" initial={false} data-id="element-94" data-mp="3pra7vcfjb0et">
        <motion.div key={step} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 12
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -12
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="p-4 sm:p-6" data-id="element-95" data-mp="3psl8x4hlhu28">
          {step === 'lens' && <>
              <h3 className={titleClass} data-id="element-96" data-mp="9yr9tsxwj3ce">Choose your lens</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-97" data-mp="38hg3s6uh6hc4">
                {state.complete ? 'Your Complete System includes all three. Pick the lens fitted on the frame.' : 'Which lens fits your work? Your X1 includes one lens of your choice.'}
              </p>
              <div role="radiogroup" aria-label="Primary lens" className="mt-5 grid grid-cols-3 gap-2 sm:gap-3" data-id="element-98" data-mp="3n6c7xsteva7g">
                {lensOptions.map(lens => {
              const selected = state.lens === lens.id;
              return <button key={lens.id} type="button" role="radio" aria-checked={selected} onClick={() => builder.chooseLens(lens.id)} className={`group relative flex flex-col border-2 text-left transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${selected ? 'border-grit-500 bg-grit-500/[0.07]' : 'border-white/12 bg-ink-950 hover:border-white/30'}`} data-id="element-99" data-mp="2uoqd3p404o8o">
                      <X1LensImage lensId={lens.id} data-id="element-100" data-mp="1zf39pztcogme" />
                      <span className="flex items-center gap-1.5 px-2 pt-2 sm:px-3" data-id="element-101" data-mp="1uimmr8xw08aw">
                        <span aria-hidden="true" className="h-3 w-3 shrink-0 border border-white/30" style={{
                    backgroundColor: lens.swatch
                  }} data-id="element-102" data-mp="1pjjjnu1gwc5k" />
                        <span className="font-display text-lg font-extrabold uppercase leading-none text-offwhite sm:text-xl" data-id="element-103" data-mp="1gk6fngw44fs8">
                          {lens.name}
                        </span>
                      </span>
                      <span className="px-2 pb-2.5 pt-1 text-[11px] leading-snug text-steel-400 sm:px-3 sm:text-[13px]" data-id="element-104" data-mp="wwjqyiifo8au">
                        {lens.blurb}
                      </span>
                      {selected && <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center bg-grit-500 text-ink-950" data-id="element-105" data-mp="1pkpfzhgisjgh">
                          <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-106" data-mp="1tlrc3pf54fe8" />
                        </span>}
                    </button>;
            })}
              </div>
              <p className="mt-4 text-[13px] text-steel-400" data-id="element-107" data-mp="28k3b42pzk08k">Tap each lens to preview it on your X1.</p>
              <div className="mt-3 flex" data-id="element-108" data-mp="2da9zc1gl5iqa">
                <button type="button" onClick={builder.nextStep} className="inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-109" data-mp="2dc35lfnae0xr">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-110" data-mp="23rizmtp6roef" />
                  I want {primaryLens?.name}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-111" data-mp="1zqz05jh548p4" />
                </button>
              </div>
            </>}

          {step === 'lensSystem' && <>
              {state.fullLens || state.complete ? <>
                  <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-grit-500" data-id="element-112" data-mp="3p2iy7uijxeir">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-113" data-mp="3k6tvxjf5djir" />
                    {state.complete ? 'Included in Complete' : `Full Lens System added · +${money(builderPrices.fullLensSystem)}`}
                  </p>
                  <h3 className={`${titleClass} mt-2`} data-id="element-114" data-mp="1rfq8rsevgafa">
                    All 3 lenses. <span className="text-grit-500" data-id="element-115" data-mp="1ref9a4sglotz">One X1.</span>
                  </h3>
                  <p className="mt-2 text-[15px] text-steel-300" data-id="element-116" data-mp="1w91jw5qpvdd0">
                    {remainingLenses(state.lens).map(lens => lens.name).join(' + ')}{' '}
                    join your {primaryLens?.name} lens. Swap lenses to match the job.
                  </p>
                </> : <>
                  <h3 className={titleClass} data-id="element-117" data-mp="2f4j6nuf20scj">Want all 3 lenses?</h3>
                  <p className="mt-2 text-[15px] text-steel-300" data-id="element-118" data-mp="ft8gtpggvlmi">
                    Add the remaining two interchangeable lenses. Swap on the job instead of buying another pair.
                  </p>
                </>}
              {/* Real studio shot of all three lenses, labelled against the customer's pick */}
              <figure className={`mt-4 overflow-hidden border-2 bg-[#0e0e0f] transition-colors duration-200 ease-out ${state.fullLens || state.complete ? 'border-grit-500' : 'border-white/10'}`} data-id="element-119" data-mp="8fzemwc4rxjf">
                <div className="relative aspect-[5/2] overflow-hidden" data-id="element-120" data-mp="w7obmpzwda6p">
                  <img src={builderAssets.fullLensSystem} alt="GritVue X1 with the Smoke, Amber and Clear lenses side by side" loading="lazy" className="absolute inset-x-0 top-1/2 w-full -translate-y-[52%]" data-id="element-121" data-mp="r81z6irw7xyy" />
                </div>
                <figcaption className="grid grid-cols-3 border-t border-white/10" data-id="element-122" data-mp="36zpkti1gi42g">
                  {(['smoke', 'amber', 'clear'] as const).map(id => {
                const option = lensOptions.find(lens => lens.id === id);
                const isPrimary = id === state.lens;
                const added = state.complete || state.fullLens;
                return <span key={id} className="flex flex-col items-center gap-0.5 border-l border-white/10 px-2 py-2.5 first:border-l-0" data-id="element-123" data-mp="2ws7akn6d5p5n">
                        <span className="flex items-center gap-1.5 font-display text-[15px] font-extrabold uppercase tracking-wide text-offwhite" data-id="element-124" data-mp="2rze680et4iu3">
                          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border border-white/40" style={{
                      backgroundColor: option?.swatch
                    }} data-id="element-125" data-mp="31r0pmk1ato7x" />
                          {option?.name}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.16em] ${isPrimary ? 'text-steel-300' : added ? 'text-grit-500' : 'text-steel-500'}`} data-id="element-126" data-mp="1ldk1s95f9sqs">
                          {isPrimary ? 'Your lens' : added ? '✓ Added' : '+ Add'}
                        </span>
                      </span>;
              })}
                </figcaption>
              </figure>
              <Decision builder={builder} kind="lensSystem" selected={state.fullLens} included={state.complete} addLabel="Add full lens system" declineLabel="No thanks" price={builderPrices.fullLensSystem} data-id="element-127" data-mp="3n8hra7vwc01t" />
            </>}

          {step === 'case' && <>
              <h3 className={titleClass} data-id="element-128" data-mp="34bgl4jqlvtwa">Protect your X1</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-129" data-mp="yhyr9994xdjx">Toolbox, truck seat, jobsite floor. Keep it in one piece.</p>
              <div className="mt-5" data-id="element-130" data-mp="12iiosdxmej6l">
                <BuilderOptionCard title="Hard protective case" body="Semi-rigid zippered case sized for the X1 and its lenses." price={builderPrices.hardCase} selected={state.hardCase} included={state.complete} onToggle={() => builder.setHardCase(!state.hardCase)} media={<Thumb src={builderAssets.hardCase} alt="" data-id="element-132" data-mp="1xitx4tgzx29g" />} data-id="element-131" data-mp="12hh9fx9knaj2" />
              </div>
              <Decision builder={builder} kind="case" selected={state.hardCase} included={state.complete} addLabel="Add case" declineLabel="Skip" price={builderPrices.hardCase} data-id="element-133" data-mp="2u9ckvwvewb2d" />
            </>}

          {step === 'care' && <>
              <h3 className={titleClass} data-id="element-134" data-mp="3dwse3s48mvua">Keep your X1 clear</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-135" data-mp="393zc3c05m1lw">
                {state.complete ? 'Not part of the Complete System. Add it if you want it.' : 'Optional. Add it if you want it.'}
              </p>
              <div className="mt-5" data-id="element-136" data-mp="3ldyquih3635n">
                <BuilderOptionCard title="GritVue anti-fog care kit" body="Mini anti-fog / lens cleaner and a microfiber cleaning cloth." price={builderPrices.careKit} selected={state.careKit} onToggle={() => builder.setCareKit(!state.careKit)} media={<Thumb src={builderAssets.careKit} alt="" data-id="element-138" data-mp="87osm7bwijwf" />} data-id="element-137" data-mp="3lf08j5slxnwc" />
              </div>
              <Decision builder={builder} kind="care" selected={state.careKit} included={false} addLabel="Add care kit" declineLabel="Skip" price={builderPrices.careKit} data-id="element-139" data-mp="cyeyrms5iqs6" />
            </>}

          {step === 'rx' && <>
              <div className="flex items-center gap-3" data-id="element-140" data-mp="qwd8gevj1z1c">
                <RxMark className="h-10 w-10 shrink-0" data-id="element-141" data-mp="dao9m2tqt8fy" />
                <h3 className={titleClass} data-id="element-142" data-mp="9ankno90peun">Need prescription vision?</h3>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-143" data-mp="n1snt0blgp9q">
                The removable GritVue RX Insert fits behind the main X1 lens. Take the insert to your local optical
                professional and have your prescription lenses fitted into it. Then reinstall it in your X1.
              </p>
              <div className="mt-5" data-id="element-144" data-mp="3mhfin602pugj">
                <BuilderOptionCard title="RX capability" body="Removable · Optical-shop ready · Built for the X1. Prescription lenses are not included." price={builderPrices.rxInsert} selected={state.rx > 0} onToggle={() => builder.setRx(state.rx > 0 ? 0 : 1)} media={<Thumb src={builderAssets.rxInsert} alt="" data-id="element-146" data-mp="1arwhaev2j8n0" />} data-id="element-145" data-mp="3miib3id1o4yk" />
              </div>
              <Decision builder={builder} kind="rx" selected={state.rx > 0} included={false} addLabel="Add RX" declineLabel="Skip" price={builderPrices.rxInsert} data-id="element-147" data-mp="2soi0ujk85i69" />
            </>}

          {step === 'protection' && <>
              <h3 className={titleClass} data-id="element-148" data-mp="3ccz6amlsyvhh">Choose your coverage</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-149" data-mp="3cdilvwnzd7sc">
                Basic coverage is included. Extra protection is optional.{' '}
                <Link to="/legal/guarantee" className="font-semibold text-grit-500 underline-offset-2 hover:underline" data-id="element-150" data-mp="32nflvcifytki">
                  Terms apply
                </Link>
                .
              </p>
              <div role="radiogroup" aria-label="Protection" className="mt-5 grid gap-3" data-id="element-151" data-mp="3vbbg8akjg9e4">
                {(state.complete ? completeProtection : essentialProtection).map(option => <BuilderOptionCard key={option.id} role="radio" title={option.name} body={option.body} price={option.price} selected={state.protection === option.id} onToggle={() => builder.setProtection(option.id)} data-id="element-152" data-mp="dt5ykwhvb48q" />)}
              </div>
              <div className="mt-5 flex" data-id="element-153" data-mp="3r07nq55pfx1w">
                <button type="button" onClick={builder.nextStep} className="inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-154" data-mp="3r1914ga70vms">
                  Review my X1
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-155" data-mp="33c7u8xh0dsjm" />
                </button>
              </div>
            </>}

          {step === 'review' && <>
              <h3 className={titleClass} data-id="element-156" data-mp="1mvr02fwcji29">Review your X1</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-157" data-mp="1rtskb45idnfb">Everything you chose, exactly as it ships.</p>
              <ul className="mt-5 grid gap-2 border-y border-white/10 py-4" aria-label="Your build" data-id="element-158" data-mp="1wqizzqdeih07">
                {lines.map(line => <li key={line.label} className="flex items-start justify-between gap-3 text-[15px]" data-id="element-159" data-mp="z28wt81vpn7i">
                    <span className="flex items-start gap-2 text-offwhite" data-id="element-160" data-mp="1929iv1r4uhfe">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-161" data-mp="1906gdi8smvts" />
                      {line.label}
                      {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                    </span>
                    <span className="shrink-0 tabular-nums text-steel-300" data-id="element-162" data-mp="lcf2wbbiocqx">{money(line.price * line.quantity)}</span>
                  </li>)}
                <li className="flex items-start gap-2 text-[14px] text-steel-400" data-id="element-163" data-mp="3av0k6xmgqr9t">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-steel-500" aria-hidden="true" data-id="element-164" data-mp="35x8gl6cghgps" />
                  {protectionLabel(state)}
                </li>
              </ul>
              <div className="mt-4 flex items-end justify-between" data-id="element-165" data-mp="3evao84qp37m1">
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-166" data-mp="3ttcwutcl2ja5">Total</span>
                <span className="font-display text-5xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-167" data-mp="3ov1m8gegs4qv">
                  {money(total)}
                </span>
              </div>
              <p className="mt-2 flex items-center gap-2 text-[14px] font-semibold text-grit-500" data-id="element-168" data-mp="2f74um3vbluc">
                <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-169" data-mp="geghjbsv7xtk" />
                Free U.S. shipping
              </p>
              <button type="button" onClick={builder.checkout} disabled={builder.routing} className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-170" data-mp="bger25t40w82">
                {builder.routing ? 'Opening cart' : 'Checkout'}
                {builder.routing ? <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-171" data-mp="h3qpa29e5r4t" /> : <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-172" data-mp="2awvdbtdh8gm" />}
              </button>
              <div className="mt-3" data-id="element-173" data-mp="3phfo8wcwi534">
                <NotConnected builder={builder} data-id="element-174" data-mp="3piii9d5isnn9" />
              </div>
              <SecureCheckoutNote className="mt-4" data-id="element-175" data-mp="3tiswfd3gukd7" />
            </>}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-3 sm:px-6" data-id="element-176" data-mp="2jps4faji8yz9">
        <button type="button" onClick={builder.prevStep} disabled={index === 0} className="inline-flex items-center gap-1.5 px-2 py-2 text-[12px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite disabled:invisible focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-177" data-mp="2oq62bzre6sjk">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-178" data-mp="37ektqo5mg8kw" />
          Back
        </button>
      </div>
    </div>;
}